import React, { useEffect } from 'react'
import { mapInboxMessage } from '../../lib/utils/mappers/inboxMapper'
import {
	InboxItem,
	InboxNotificationCategory,
	InboxNotificationObjectType,
} from '../../lib/api/types/inbox.interfaces'
import cn from '../../lib/utils/cn'
// import { useToggleInboxRead } from '../../hooks/api/inboxHooks'
import Button from './Button'
import { useSound } from 'use-sound'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'

interface NotificationToastProps {
	notification: InboxItem
	onClose: () => void
	className?: string
	openButton?: {
		label?: string
		tooltip?: string
		onOpen: () => void
	} | null
}

const NotificationToast: React.FC<NotificationToastProps> = ({
	notification,
	onClose,
	className,
	openButton = null,
}) => {
	// const { mutateAsync: toggleReadStatus, isPending: isTogglingRead } =
	// 	useToggleInboxRead()

	const { message, icon } = mapInboxMessage(notification)

	// const { isRead, id } = notification

	const truncatedMessage =
		message?.length > 73 ? message?.substring(0, 73)?.trim() + '...' : message

	return (
		<div
			className={cn(
				'bg-white dark:bg-background-dark rounded-lg w-full animate-in slide-in-from-right-2 duration-300',
				className
			)}
		>
			{/* Header */}
			<div className="flex items-start justify-between mb-3">
				<div className="flex items-center gap-2">
					{icon}
					<div className="flex flex-col">
						<span className="text-sm font-medium text-text-main dark:text-text-main-dark">
							{notification.from?.name || 'System'}
						</span>
						<span className="text-xs text-text-secondary dark:text-text-secondary-dark max-w-full truncate line-clamp-2">
							{new Date(notification.createdAt).toLocaleString()}
						</span>
					</div>
				</div>
			</div>

			{/* Message */}
			<div className="mb-4">
				<p className="text-sm text-text-main dark:text-text-main-dark leading-relaxed">
					{truncatedMessage}
				</p>
			</div>

			{/* Actions */}
			<div className="flex items-center justify-between ">
				<div className="flex items-center gap-2">
					<Button variant="tertiary" label={'Close'} onClick={onClose} />
					{!!openButton && (
						<Button
							tooltip={openButton?.tooltip || null}
							label={openButton?.label || 'Open'}
							onClick={() => {
								openButton?.onOpen()
							}}
						/>
					)}
				</div>
			</div>
		</div>
	)
}

// player
export const SoundPlayer = ({ src }: { src: string }): null => {
	const [play] = useSound(src, { volume: 0.7 })

	useEffect(() => {
		play()
	}, [play])

	return null
}

// caller component
interface NotificationCallerProps {
	notification: InboxItem
}

const NotificationCallerComponent: React.FC<
	NotificationCallerProps & { onClose: () => void }
> = ({ notification, onClose }) => {
	const navigate = useNavigate()
	return (
		<>
			<SoundPlayer src="/sounds/notification-1.mp3" />
			<NotificationToast
				notification={notification}
				onClose={onClose}
				openButton={
					[
						InboxNotificationCategory.File,
						InboxNotificationCategory.Task,
					]?.includes(notification?.category)
						? {
								label:
									notification?.category === InboxNotificationCategory.File
										? 'Open File'
										: 'Open Task',
								tooltip:
									notification?.category === InboxNotificationCategory.File
										? 'Open in new tab'
										: 'Go to board page',
								onOpen: () => {
									const item = notification?.objects?.find((i) =>
										[
											InboxNotificationObjectType.TodoCard,
											InboxNotificationObjectType.File,
										]?.includes(i?.type)
									)

									if (!item?.id) return

									if (
										item.type === InboxNotificationObjectType.TodoCard &&
										item.boardId
									) {
										navigate(
											`/my-space/boards/${item.boardId}?taskId=${item.id}`
										)

										onClose()
										return
									}

									if (item.type === InboxNotificationObjectType.File) {
										window.open(
											`/aura/preview?is_blank=true&id=${item.id}`,
											'_blank'
										)
										navigate(`/preview?id=${item.id}`)
										return
									}
								},
						  }
						: null
				}
			/>
		</>
	)
}

// caller function
export const notificationCaller = ({
	notification,
}: NotificationCallerProps) => {
	const toastId = toast(
		<NotificationCallerComponent
			notification={notification}
			onClose={() => toast.dismiss(toastId)}
		/>,
		{
			hideProgressBar: false,
			pauseOnHover: true,
			position: 'top-right',
			autoClose: 4000,
			className:
				'bg-white shadow-xl rounded-xl -mt-1 border border-stroke-dark dark:bg-background-dark',
			bodyClassName: 'text-gray-800',
			progressStyle: { background: '#ff841b', height: '4px' },
		}
	)

	// Return the toastId so the caller can dismiss it if needed
	return toastId
}
